import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { FaStar } from "react-icons/fa";
import { IoTimeOutline } from "react-icons/io5";
import { StoreContext } from "../context/StoreContext";
import { isRestaurantClosed, sortRestaurantsByOpenStatus, getClosingSoonMessage } from "../utils/restaurantHours";

const CITY_COORDINATES = {
  chandigarh: { lat: 30.733432106722248, lng: 76.77966363905182 },
  mumbai_pune_way: { lat: 18.79257164100469, lng: 73.2957764173606 },
};

const RestaurantSuggestions = () => {
  const [searchParams] = useSearchParams();
  const { citySlug } = useParams();
  const navigate = useNavigate();

  const { setRoadId, userId, accessToken, transientUser } =
    useContext(StoreContext);

  const locationParam = searchParams.get("location");
  const sessionId = searchParams.get("sessionId");

  const cityCoords = citySlug ? CITY_COORDINATES[citySlug.toLowerCase()] : null;
  const hideETA = Boolean(cityCoords);

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const parsedLocation = useMemo(() => {
    if (cityCoords) return cityCoords;
    if (!locationParam) return null;
    const [latRaw, lngRaw] = locationParam.split(",").map((c) => c.trim());
    const latNum = parseFloat(latRaw);
    const lngNum = parseFloat(lngRaw);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
    return { lat: latNum, lng: lngNum };
  }, [locationParam, cityCoords]);

  useEffect(() => {
    const fetchRestaurants = async () => {
      setError(null);

      if (!parsedLocation) {
        setError("Location is missing or invalid.");
        setLoading(false);
        return;
      }

      try {
        const payload = {
          mode: "AROUND_ME",
          userContexts: [],
          lastItemId: 0,
          pageSize: 40,
          serviceInfo: {
            type: "RESTAURANT",
            filters: [],
            distanceTimeCalculator: cityCoords ? "HAVERSINE" : "GOOGLE",
          },
          location: {
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
          },
          radiusInMeter: 10000,
        };

        const response = await axios.post(
          "/api/catalogue/discovery/places",
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        const raw = response?.data?.data?.data || [];
        const flattened = raw.flat().filter(Boolean);
        setRestaurants(sortRestaurantsByOpenStatus(flattened));
      } catch (e) {
        console.error("Failed to fetch restaurants:", e);
        setError("Could not load restaurants right now. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, [parsedLocation, cityCoords]);

  const handleRestaurantClick = (restaurant) => {
    if (restaurant?.positionGeohash) {
      setRoadId(restaurant.positionGeohash);
    }
    try {
      if (restaurant?.placeId) {
        localStorage.setItem(
          "supportedServeTypesRestaurantId",
          restaurant.placeId
        );
      }
      if (Array.isArray(restaurant?.supportedServeTypes)) {
        localStorage.setItem(
          "supportedServeTypes",
          JSON.stringify(restaurant.supportedServeTypes)
        );
      } else {
        localStorage.removeItem("supportedServeTypes");
      }
    } catch (e) {
      // ignore storage errors
    }

    navigate(`/menu-items/${restaurant.placeId}`, {
      state: {
        restaurantId: restaurant.placeId,
        name: restaurant.name,
        shortAddress: restaurant.shortAddress,
        distance: restaurant.distance,
        rating: restaurant.rating,
        ratingCount: restaurant.ratingCount,
        readableETA: hideETA ? null : restaurant.readableETA,
        opensAt: restaurant.opensAt,
        closesAt: restaurant.closesAt,
        positionGeohash: restaurant.positionGeohash,
        location: locationParam,
        sessionId,
        userId,
        accessToken,
        transientUser,
        supportedServeTypes: restaurant.supportedServeTypes,
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <TailSpin
          height="80"
          width="80"
          color="#FBC02D"
          ariaLabel="loading"
          radius="1"
          visible={true}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen flex flex-col justify-center items-center px-4 text-center space-y-4">
        <h2 className="text-2xl font-semibold text-gray-800">Oops</h2>
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-[#FBC02D] text-white px-6 py-3 rounded-lg hover:bg-[#F9A825] transition-colors font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex-col justify-center pt-24 px-4">
      <h1 className="flex items-center h-12 mb-4 tracking-wide font-semibold text-lg">
        Restaurants around you
      </h1>
      {restaurants.length === 0 && (
        <p className="text-gray-600">No restaurants found nearby.</p>
      )}
      <div className="space-y-6">
        {restaurants.map((restaurant) => {
          const closed = isRestaurantClosed(restaurant);
          const closingSoonLabel = !closed ? getClosingSoonMessage(restaurant) : null;
          return (
            <div
              key={restaurant.placeId}
              onClick={() => handleRestaurantClick(restaurant)}
              className={`flex pb-4 border-b border-gray-200 cursor-pointer ${
                closed ? "grayscale opacity-60" : ""
              }`}
            >
              <div className="w-[30%] md:w-[20%] h-full">
                {restaurant.thumbnailUrl ? (
                  <img
                    src={restaurant.thumbnailUrl}
                    alt={restaurant.name}
                    className="rounded-3xl w-full md:w-40 h-28 object-cover"
                  />
                ) : (
                  <div className="rounded-3xl w-full md:w-40 h-28 bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
                    No image
                  </div>
                )}
              </div>
              <div className="w-[70%] md:w-[80%] flex flex-col justify-center pl-4 space-y-1">
                <h2 className="font-semibold text-xl">{restaurant.name}</h2>
                <div className="flex items-center text-sm text-gray-700">
                  <FaStar className="text-yellow-400 mr-1" />
                  <span className="font-bold">{restaurant.rating?.toFixed?.(1) ?? restaurant.rating ?? 0}</span>
                  <span className="text-gray-500 ml-2">({restaurant.ratingCount || 0})</span>
                  {!hideETA && restaurant.readableETA && (
                    <span className="text-green-700 font-semibold ml-3">
                      {restaurant.readableETA}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {restaurant.tags?.length ? restaurant.tags.join(", ") : "Restaurant"}
                </p>
                <p className="text-sm text-gray-600">
                  {restaurant.shortAddress || "Nearby"} | {restaurant.distance || ""}
                </p>
                {closingSoonLabel && (
                  <span className="inline-flex items-center gap-1 w-fit text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    <IoTimeOutline className="h-3.5 w-3.5" />
                    {closingSoonLabel}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RestaurantSuggestions;
